import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import { useAuth } from '../AuthContext';
import { getSkiRequests, createSkiRequest, submitSkiRequest, getSkiRequestSubmissions, getSkis, deleteSkiRequest } from '../api';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImageIcon from '@mui/icons-material/Image';
import html2canvas from 'html2canvas';

// Countdown component for ski request deadlines
function CountdownTimer({ deadline }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const deadlineTime = new Date(deadline).getTime();
      const difference = deadlineTime - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [deadline]);

  if (isExpired) {
    return (
      <Chip
        icon={<AccessTimeIcon />}
        label="OVERDUE"
        color="error"
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  }

  return (
    <Chip
      icon={<AccessTimeIcon />}
      label={
        timeLeft.days > 0
          ? `${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m`
          : timeLeft.hours > 0
          ? `${timeLeft.hours}h ${timeLeft.minutes}m`
          : `${timeLeft.minutes}m ${timeLeft.seconds}s`
      }
      color={timeLeft.days === 0 && timeLeft.hours < 24 ? "warning" : "primary"}
      size="small"
      sx={{ fontWeight: 'bold' }}
    />
  );
}

function isCoach(membership) {
  return membership && (membership.role === 'coach' || membership.role === 'both');
}
function isAthlete(membership) {
  return membership && (membership.role === 'athlete' || membership.role === 'both');
}

export default function SkiRequestsSection({ team, eventId, onRequestSubmitted }) {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [submissionsOpen, setSubmissionsOpen] = useState(null); // requestId or null
  const [membership, setMembership] = useState(null);
  const [skis, setSkis] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, requestId: null });
  const [deleting, setDeleting] = useState(false);

  // For create request (coach)
  const [form, setForm] = useState({ note: '', max_skis: 2, date: '', time: '12:00' });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const reqs = await getSkiRequests(team.id, eventId);
      setRequests(reqs);
      // Use flat role property if memberships is missing
      if (team.memberships) {
        setMembership(team.memberships.find(m => m.user_id === user.id));
      } else if (team.role) {
        setMembership({ role: team.role });
      }
      setLoading(false);
    }
    fetchData();
  }, [team, user.id, eventId]);

  // For athlete: fetch their skis
  useEffect(() => {
    if (isAthlete(membership)) {
      getSkis().then(setSkis);
    }
  }, [membership]);

  // --- Coach: Create Request ---
  const handleCreate = async () => {
    setError('');
    if (!form.date || !form.time) { setError('Date and time are required'); return; }
    setSubmitting(true);
    try {
      // Combine date and time as a local time string (no timezone conversion)
      const dateTimeString = `${form.date}T${form.time}`;
      await createSkiRequest(team.id, { ...form, date: dateTimeString, event_id: eventId });
      setCreateOpen(false);
      setForm({ note: '', max_skis: 2, date: '', time: '12:00' });
      const reqs = await getSkiRequests(team.id, eventId);
      setRequests(reqs);
    } catch (err) {
      setError(err.message || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Athlete: Submit Skis ---
  const handleSubmitSkis = async (requestId, selectedSkis) => {
    setSubmitting(true);
    setError('');
    try {
      await submitSkiRequest(requestId, selectedSkis);
      const reqs = await getSkiRequests(team.id, eventId);
      setRequests(reqs);
      // Call the callback to update notifications in parent component
      if (onRequestSubmitted) {
        onRequestSubmitted();
      }
    } catch (err) {
      setError(err.message || 'Failed to submit skis');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Coach: View Submissions ---
  const [submissions, setSubmissions] = useState([]);
  const [allSkis, setAllSkis] = useState([]); // For mapping ski IDs to numbers
  useEffect(() => {
    if (submissionsOpen) {
      getSkiRequestSubmissions(submissionsOpen).then(setSubmissions);
      // Fetch all skis for the team, not just the coach
      fetch(`/api/teams/${team.id}/athletes/all/skis`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(res => res.json())
        .then(data => Array.isArray(data) ? setAllSkis(data) : setAllSkis(data.skis || []));
    }
  }, [submissionsOpen, team.id]);
  const skiIdToNumber = Object.fromEntries(allSkis.map(s => [s.id, s.ski_number]));

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteSkiRequest(team.id, deleteDialog.requestId);
      setDeleteDialog({ open: false, requestId: null });
      const reqs = await getSkiRequests(team.id, eventId);
      setRequests(reqs);
    } catch (err) {
      setError(err.message || 'Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  const handleExportImage = async () => {
    try {
      const currentRequest = requests.find(r => r.id === submissionsOpen);
      if (!currentRequest) return;

      // Create a container for the export
      const exportContainer = document.createElement('div');
      exportContainer.style.cssText = `
        background: white;
        color: #121212;
        padding: 40px;
        font-family: 'Roboto', sans-serif;
        max-width: 800px;
        margin: 0 auto;
      `;

      // Add logo (you can replace this with your actual logo)
      const logoDiv = document.createElement('div');
      logoDiv.style.cssText = `
        text-align: center;
        margin-bottom: 30px;
        font-size: 24px;
        font-weight: bold;
        color: #1976d2;
      `;
      logoDiv.textContent = 'Ski Fleet Management';
      exportContainer.appendChild(logoDiv);

      // Add request info
      const requestInfo = document.createElement('div');
      requestInfo.style.cssText = `
        margin-bottom: 30px;
        padding: 20px;
        background: #f5f5f5;
        border-radius: 8px;
      `;
      requestInfo.innerHTML = `
        <h2 style="margin: 0 0 10px 0; color: #1976d2;">Ski Request Submissions</h2>
        <p style="margin: 5px 0;"><strong>Event:</strong> ${currentRequest.note || 'No note'}</p>
        <p style="margin: 5px 0;"><strong>Deadline:</strong> ${formatDateTime(currentRequest.date)}</p>
        <p style="margin: 5px 0;"><strong>Max Skis:</strong> ${currentRequest.max_skis}</p>
        <p style="margin: 5px 0;"><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
      `;
      exportContainer.appendChild(requestInfo);

      // Add submissions table
      if (submissions.length > 0) {
        const tableDiv = document.createElement('div');
        tableDiv.style.cssText = `
          margin-bottom: 30px;
        `;
        
        const table = document.createElement('table');
        table.style.cssText = `
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        `;

        // Table header
        const thead = document.createElement('thead');
        thead.innerHTML = `
          <tr style="background: #1976d2; color: white;">
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Athlete</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Skis Submitted</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Submitted At</th>
          </tr>
        `;
        table.appendChild(thead);

        // Table body
        const tbody = document.createElement('tbody');
        submissions.forEach((sub, index) => {
          const row = document.createElement('tr');
          row.style.cssText = `
            background: ${index % 2 === 0 ? '#f9f9f9' : 'white'};
          `;
          row.innerHTML = `
            <td style="padding: 12px; border: 1px solid #ddd;">${sub.athlete_name}</td>
            <td style="padding: 12px; border: 1px solid #ddd;">${(sub.ski_ids || []).map(id => `#${skiIdToNumber[id] || id}`).join(', ')}</td>
            <td style="padding: 12px; border: 1px solid #ddd;">${sub.submitted_at ? formatDateTime(sub.submitted_at) : ''}</td>
          `;
          tbody.appendChild(row);
        });
        table.appendChild(tbody);
        tableDiv.appendChild(table);
        exportContainer.appendChild(tableDiv);
      } else {
        const noDataDiv = document.createElement('div');
        noDataDiv.style.cssText = `
          text-align: center;
          padding: 40px;
          color: #666;
          font-style: italic;
        `;
        noDataDiv.textContent = 'No submissions yet.';
        exportContainer.appendChild(noDataDiv);
      }

      // Add footer
      const footer = document.createElement('div');
      footer.style.cssText = `
        text-align: center;
        margin-top: 30px;
        padding-top: 20px;
        border-top: 1px solid #ddd;
        color: #666;
        font-size: 12px;
      `;
      footer.textContent = `Generated by Ski Fleet Management App - ${new Date().toLocaleDateString()}`;
      exportContainer.appendChild(footer);

      // Temporarily add to DOM
      document.body.appendChild(exportContainer);

      // Capture as image
      const canvas = await html2canvas(exportContainer, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      // Clean up
      document.body.removeChild(exportContainer);

      // Download image
      const link = document.createElement('a');
      link.download = `ski_requests_${currentRequest.note?.replace(/[^a-zA-Z0-9]/g, '_') || 'submissions'}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error exporting image:', error);
      alert('Failed to export image. Please try again.');
    }
  };

  // --- UI ---
  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Ski Requests for {team.name}
      </Typography>
      {isCoach(membership) && (
        <Box sx={{ mb: 3 }}>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create Ski Request
          </Button>
        </Box>
      )}
      {requests.length === 0 ? (
        <Typography color="text.secondary">No ski requests yet.</Typography>
      ) : (
        <Stack spacing={3}>
          {requests.map(request => (
            <Paper key={request.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {formatDateTime(request.date)} — {request.note || 'No note'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isAthlete(membership) && <CountdownTimer deadline={request.date} />}
                  <Chip label={`Max Skis: ${request.max_skis}`} color="primary" />
                  {isCoach(membership) && (
                    <IconButton color="error" onClick={() => setDeleteDialog({ open: true, requestId: request.id })}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
              {isAthlete(membership) && (
                <AthleteRequestForm
                  request={request}
                  skis={skis}
                  onSubmit={handleSubmitSkis}
                  submitting={submitting}
                  userId={user.id}
                />
              )}
              {isCoach(membership) && (
                <Button size="small" onClick={() => setSubmissionsOpen(request.id)} sx={{ mt: 1 }}>
                  View Submissions
                </Button>
              )}
            </Paper>
          ))}
        </Stack>
      )}
      {/* Create Request Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create Ski Request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Time"
              type="time"
              value={form.time}
              onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Max Skis Allowed"
              type="number"
              value={form.max_skis}
              onChange={e => setForm(f => ({ ...f, max_skis: e.target.value }))}
              inputProps={{ min: 1, max: 5 }}
              fullWidth
              required
            />
            <TextField
              label="Coach Note (optional)"
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            {error && <Typography color="error">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Submissions Dialog (Coach) */}
      <Dialog open={!!submissionsOpen} onClose={() => setSubmissionsOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>Submissions</DialogTitle>
        <DialogContent>
          {submissions.length === 0 ? (
            <Typography>No submissions yet.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Athlete</TableCell>
                    <TableCell>Skis Submitted</TableCell>
                    <TableCell>Submitted At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map(sub => (
                    <TableRow key={sub.id}>
                      <TableCell>{sub.athlete_name}</TableCell>
                      <TableCell>{(sub.ski_ids || []).map(id => `#${skiIdToNumber[id] || id}`).join(', ')}</TableCell>
                      <TableCell>{sub.submitted_at ? formatDateTime(sub.submitted_at) : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleExportImage} 
            startIcon={<ImageIcon />}
            variant="outlined"
            color="primary"
          >
            Export Image
          </Button>
          <Button onClick={() => setSubmissionsOpen(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, requestId: null })}>
        <DialogTitle>Delete Ski Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this ski request? This cannot be undone.</Typography>
          {error && <Typography color="error">{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, requestId: null })} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function formatDateTime(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  if (isNaN(d)) return dt;
  return d.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function AthleteRequestForm({ request, skis, onSubmit, submitting, userId }) {
  // Find previous submission if any
  const prev = request.submissions?.find(s => s.athlete_id === userId);
  const [selected, setSelected] = useState(prev ? prev.ski_ids : []);
  const [submitted, setSubmitted] = useState(!!prev);
  const [expanded, setExpanded] = useState(false);
  const max = request.max_skis;

  // Persist collapsed view if prev exists and on mount
  useEffect(() => {
    if (prev) setSubmitted(true);
  }, [prev]);

  // Group skis by type (case-insensitive)
  const grouped = skis.reduce((acc, ski) => {
    const type = (ski.type || 'other').toLowerCase();
    if (!acc[type]) acc[type] = [];
    acc[type].push(ski);
    return acc;
  }, {});
  const displayOrder = [
    { key: 'classic', label: 'Classic Skis' },
    { key: 'skate', label: 'Skate Skis' },
    { key: 'other', label: 'Other Skis' }
  ];

  const handleCheck = (skiId) => {
    setSelected(sel =>
      sel.includes(skiId)
        ? sel.filter(id => id !== skiId)
        : sel.length < max
          ? [...sel, skiId]
          : sel
    );
  };

  const handleSubmit = async () => {
    await onSubmit(request.id, selected);
    setSubmitted(true);
    setExpanded(false);
  };

  if (submitted) {
    // Show collapsed summary view for submitted requests
    const skiNumbers = skis.filter(s => selected.includes(s.id)).map(s => `#${s.ski_number}`).join(', ');
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You submitted: {skiNumbers}
          </Typography>
          <Chip label="Submitted" color="success" size="small" />
        </Box>
        <Button variant="text" onClick={() => setExpanded(true)} sx={{ mt: 1 }}>
          Edit Submission
        </Button>
      </Box>
    );
  }

  // Show collapsed state for unsubmitted requests
  if (!expanded) {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No skis selected yet
          </Typography>
          <Chip label="Not Submitted" color="warning" size="small" />
        </Box>
        <Button variant="contained" onClick={() => setExpanded(true)} sx={{ mt: 1 }}>
          Select Skis
        </Button>
      </Box>
    );
  }

  // Show expanded form view
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select up to {max} ski{max > 1 ? 's' : ''}:
      </Typography>
      {displayOrder.map(({ key, label }) =>
        grouped[key] && grouped[key].length > 0 && (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ski #</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell align="center">Select</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped[key].map(ski => (
                    <TableRow key={ski.id}>
                      <TableCell>{ski.ski_number}</TableCell>
                      <TableCell>{ski.brand}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={selected.includes(ski.id)}
                          onChange={() => handleCheck(ski.id)}
                          disabled={submitting && !selected.includes(ski.id)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      )}
      {/* Show any unexpected types not in displayOrder */}
      {Object.entries(grouped).map(([key, skis]) =>
        !displayOrder.some(d => d.key === key) && skis.length > 0 && (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{key.charAt(0).toUpperCase() + key.slice(1)} Skis</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ski #</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell align="center">Select</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skis.map(ski => (
                    <TableRow key={ski.id}>
                      <TableCell>{ski.ski_number}</TableCell>
                      <TableCell>{ski.brand}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={selected.includes(ski.id)}
                          onChange={() => handleCheck(ski.id)}
                          disabled={submitting && !selected.includes(ski.id)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      )}
      <Button
        variant="contained"
        sx={{ mt: 2 }}
        disabled={submitting || selected.length === 0}
        onClick={handleSubmit}
      >
        {submitting ? <CircularProgress size={20} /> : 'Submit'}
      </Button>
      <Button
        variant="outlined"
        sx={{ mt: 2, ml: 1 }}
        onClick={() => setExpanded(false)}
      >
        Cancel
      </Button>
      {prev && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Last submitted: {prev.ski_ids.map(id => `#${id}`).join(', ')}
        </Typography>
      )}
    </Box>
  );
} 